import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { DismantlingCoefficientTypeEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Button, Select, IconEye, IconEdit } from 'uikit';

import type React from 'react';
import { useEffect, useCallback, useMemo, useState } from 'react';

import { UerSelectionModal } from './components/UerSelectionModal';

import Styles from './GroupTypesUerSelector.module.css';

interface IGroupTypesUerSelector {
    label?: string;
    disabled?: boolean;
    onGroupTypeChange?: (groupType?: DismantlingCoefficientTypeEnum) => void;
    status: 'alert' | 'warning' | 'success' | undefined;
    disabledIds?: number[];
    groupType?: DismantlingCoefficientTypeEnum;
    contractId: number;
    onCheckUerChange?: (checkedIds: number[]) => void;
    checkedUerIds?: number[];
    isSelectDisabled?: boolean;
    uerId: number;
}

const dismantlingCoefficientTypeItems = [
    {
        id: DismantlingCoefficientTypeEnum.EquipmentInstallation,
        label: 'Монтаж оборудования (монтажные работы)',
    },
    {
        id: DismantlingCoefficientTypeEnum.ConstructionWorks,
        label: 'Строительные и специальные строительные работы',
    },
];

export const GroupTypesUerSelector: React.FC<IGroupTypesUerSelector> = (props) => {
    const {
        label,
        disabled,
        isSelectDisabled,
        onGroupTypeChange,
        status,
        groupType,
        contractId,
        onCheckUerChange,
        checkedUerIds,
        uerId,
    } = props;

    const [checkedIds, setCheckedIds] = useState<number[]>([]);
    const [isSelectUerModalOpen, setIsSelectUerModalOpen] = useState(false);

    const onUerModalOpen = useCallback(() => {
        setIsSelectUerModalOpen(true);
    }, []);

    const onUerModalClose = useCallback(() => {
        setIsSelectUerModalOpen(false);
    }, []);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onUerModalClose,
        });

    const selectedGroupTypeItem = useMemo(
        () => dismantlingCoefficientTypeItems.find((c) => c.id === groupType),
        [groupType],
    );

    const isReadOnly = useMemo(
        () => selectedGroupTypeItem === dismantlingCoefficientTypeItems[0],
        [selectedGroupTypeItem],
    );

    const title = useMemo(
        () => `${isReadOnly ? 'Просмотр' : 'Добавление'} области применения коэффициента`,
        [isReadOnly],
    );

    const onConfirmAndClose = useCallback(() => {
        if (onCheckUerChange) {
            onCheckUerChange(checkedIds);
        }
        setIsSelectUerModalOpen(false);
    }, [checkedIds, onCheckUerChange]);

    const onModalClose = useCallback(
        (isConfirm?: boolean) => {
            if (isConfirm) {
                onConfirmAndClose();
            } else if (String(checkedIds) !== String(checkedUerIds)) {
                handleOpenConfirmationDialog();
            } else {
                onUerModalClose();
            }
        },
        [checkedIds, checkedUerIds, handleOpenConfirmationDialog, onConfirmAndClose, onUerModalClose],
    );

    const onGroupTypeChangeLocal = useCallback(
        ({ value }) => {
            onGroupTypeChange?.(value?.id);
        },
        [onGroupTypeChange],
    );

    const onCheckChangeLocal = useCallback((newCheckedIds: number[]) => {
        setCheckedIds(newCheckedIds);
    }, []);

    useEffect(() => {
        if (isSelectUerModalOpen) {
            setCheckedIds(checkedUerIds || []);
        }
    }, [checkedUerIds, isSelectUerModalOpen]);

    return (
        <div>
            <div className={Styles.groupTypesUerSelector}>
                <Select
                    label={label}
                    status={status}
                    disabled={isSelectDisabled || disabled}
                    value={selectedGroupTypeItem}
                    onChange={onGroupTypeChangeLocal}
                    labelPosition="left"
                    size="s"
                    items={dismantlingCoefficientTypeItems}
                />
                <Button
                    view="ghost"
                    type="button"
                    iconLeft={isReadOnly ? IconEye : IconEdit}
                    size="s"
                    disabled={!groupType || (!isReadOnly && disabled)}
                    onClick={onUerModalOpen}
                    className={Styles.groupTypesUerSelectorButton}
                />
            </div>
            {groupType === DismantlingCoefficientTypeEnum.ConstructionWorks && (
                <div className={Styles.groupTypesUerSelectorSelected}>Выбрано расценок: {checkedUerIds?.length}</div>
            )}
            {groupType && isSelectUerModalOpen && (
                <UerSelectionModal
                    groupType={groupType}
                    contractId={contractId}
                    uerId={uerId}
                    isReadOnly={isReadOnly}
                    onClose={onModalClose}
                    checkedIds={checkedIds}
                    onCheckChange={onCheckChangeLocal}
                    title={title}
                />
            )}
            <ConfirmationDialog
                textHeader={title}
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </div>
    );
};

GroupTypesUerSelector.displayName = 'GroupTypesUerSelector';
